import { getBlockTitle } from './dialect.ts'
import { splitFrontMatter } from './front-matter.ts'
import { extractVariables } from './variables.ts'

/**
 * Comparação entre duas versões de um documento (docs/areas/documentos-e-assinaturas.md, RN-DOC-45).
 *
 * Duas leituras da mesma diferença:
 * - `summary`: o que mudou por cláusula (`##`, `###`), por bloco do dialeto antes da primeira seção,
 *   por variável e por campo do front matter. É o que a pessoa lê antes de publicar;
 * - `lines`: o texto linha a linha, com as palavras que saíram e entraram marcadas.
 *
 * Roda igual no navegador e na API: não depende de DOM nem do parser.
 */

export type DiffOp = 'equal' | 'added' | 'removed'

export interface DiffPart {
  op: DiffOp
  text: string
}

export interface DiffLine {
  /** `changed`: a linha existe nos dois lados com texto diferente; `parts` marca as palavras. */
  op: DiffOp | 'changed'
  /** Número da linha em cada lado (a partir de 1); `null` quando a linha não existe naquele lado. */
  beforeLine: number | null
  afterLine: number | null
  before: string | null
  after: string | null
  /** Só em `changed`: as palavras de cada lado, com as que diferem marcadas. */
  beforeParts?: DiffPart[]
  afterParts?: DiffPart[]
}

export type SummaryKind = 'section' | 'block' | 'variable' | 'front_matter'
export type SummaryStatus = 'added' | 'removed' | 'changed' | 'moved'

export interface SummaryEntry {
  kind: SummaryKind
  status: SummaryStatus
  /** "2.1 Retoque" · "Em resumo" · "{{patient.rg}}" · "Título". */
  label: string
  /** Linha no documento de destino (ou no de origem, quando saiu), para rolar até a mudança. */
  line: number | null
}

export interface DocumentDiff {
  isIdentical: boolean
  summary: SummaryEntry[]
  lines: DiffLine[]
  stats: { addedLines: number; removedLines: number; changedLines: number }
}

// Sequências ------------------------------------------------------------------------------------------

/**
 * Diferença entre duas sequências (Myers, O(ND)). Devolve as operações na ordem do texto, com
 * as remoções antes das inserções de um mesmo trecho.
 */
export function diffSequences<T>(a: readonly T[], b: readonly T[]): { op: DiffOp; value: T }[] {
  // O começo e o fim iguais ficam fora da conta: entre duas versões, quase tudo é igual.
  let start = 0
  while (start < a.length && start < b.length && a[start] === b[start]) start++
  let endA = a.length
  let endB = b.length
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--
    endB--
  }

  const n = endA - start
  const m = endB - start
  const max = n + m
  const offset = max + 1
  const v = new Int32Array(2 * max + 3)
  /** O estado antes de cada rodada, só na faixa que a rodada alcança (diagonais -d-1 a d+1). */
  const trace: Int32Array[] = []

  let found = max === 0
  for (let d = 0; d <= max && !found; d++) {
    trace.push(v.slice(offset - d - 1, offset + d + 2))
    for (let k = -d; k <= d; k += 2) {
      let x: number
      if (k === -d || (k !== d && v[offset + k - 1]! < v[offset + k + 1]!)) x = v[offset + k + 1]!
      else x = v[offset + k - 1]! + 1
      let y = x - k
      while (x < n && y < m && a[start + x] === b[start + y]) {
        x++
        y++
      }
      v[offset + k] = x
      if (x >= n && y >= m) {
        found = true
        break
      }
    }
  }

  const middle: { op: DiffOp; value: T }[] = []
  let x = n
  let y = m
  for (let d = trace.length - 1; d >= 0; d--) {
    const snapshot = trace[d]!
    const local = d + 1
    const k = x - y
    const prevK =
      k === -d || (k !== d && snapshot[local + k - 1]! < snapshot[local + k + 1]!) ? k + 1 : k - 1
    const prevX = snapshot[local + prevK]!
    const prevY = prevX - prevK
    while (x > prevX && y > prevY) {
      middle.push({ op: 'equal', value: a[start + x - 1]! })
      x--
      y--
    }
    if (d > 0) {
      if (x === prevX) middle.push({ op: 'added', value: b[start + y - 1]! })
      else middle.push({ op: 'removed', value: a[start + x - 1]! })
    }
    x = prevX
    y = prevY
  }
  middle.reverse()

  const out: { op: DiffOp; value: T }[] = []
  for (let i = 0; i < start; i++) out.push({ op: 'equal', value: a[i]! })
  out.push(...middle)
  for (let i = endA; i < a.length; i++) out.push({ op: 'equal', value: a[i]! })

  // Dentro de um mesmo trecho alterado, remoções primeiro: é a ordem em que se lê.
  const ordered: { op: DiffOp; value: T }[] = []
  let removed: { op: DiffOp; value: T }[] = []
  let added: { op: DiffOp; value: T }[] = []
  const flush = () => {
    ordered.push(...removed, ...added)
    removed = []
    added = []
  }
  for (const item of out) {
    if (item.op === 'equal') {
      flush()
      ordered.push(item)
    } else if (item.op === 'removed') removed.push(item)
    else added.push(item)
  }
  flush()
  return ordered
}

/** Palavras e os espaços entre elas, para a marcação dentro da linha. */
function tokenize(line: string): string[] {
  return line.match(/\s+|[\p{L}\p{N}_]+|[^\s\p{L}\p{N}_]/gu) ?? []
}

function mergeParts(parts: DiffPart[]): DiffPart[] {
  const out: DiffPart[] = []
  for (const part of parts) {
    const last = out[out.length - 1]
    if (last && last.op === part.op) last.text += part.text
    else out.push({ ...part })
  }
  return out
}

/** As palavras de cada lado de uma linha alterada, e quanto do texto ficou igual (0 a 1). */
export function diffWords(before: string, after: string) {
  const ops = diffSequences(tokenize(before), tokenize(after))
  const beforeParts: DiffPart[] = []
  const afterParts: DiffPart[] = []
  let equal = 0
  for (const { op, value } of ops) {
    if (op === 'equal') {
      beforeParts.push({ op, text: value })
      afterParts.push({ op, text: value })
      equal += value.length
    } else if (op === 'removed') beforeParts.push({ op, text: value })
    else afterParts.push({ op, text: value })
  }
  const longest = Math.max(before.length, after.length)
  return {
    beforeParts: mergeParts(beforeParts),
    afterParts: mergeParts(afterParts),
    similarity: longest === 0 ? 1 : equal / longest,
  }
}

/** Abaixo disto a linha é outra, não a mesma reescrita: sai como remoção mais inserção. */
const MIN_SIMILARITY = 0.4

/** O texto linha a linha, com as linhas reescritas pareadas e as palavras marcadas. */
export function diffLines(before: string, after: string): DiffLine[] {
  const ops = diffSequences(before.split('\n'), after.split('\n'))
  const lines: DiffLine[] = []
  let beforeLine = 0
  let afterLine = 0

  for (let i = 0; i < ops.length;) {
    const current = ops[i]!
    if (current.op === 'equal') {
      beforeLine++
      afterLine++
      lines.push({
        op: 'equal',
        beforeLine,
        afterLine,
        before: current.value,
        after: current.value,
      })
      i++
      continue
    }
    // Um trecho alterado: as remoções seguidas das inserções.
    const removed: string[] = []
    const added: string[] = []
    while (i < ops.length && ops[i]!.op === 'removed') removed.push(ops[i++]!.value)
    while (i < ops.length && ops[i]!.op === 'added') added.push(ops[i++]!.value)

    const paired = Math.min(removed.length, added.length)
    for (let p = 0; p < paired; p++) {
      const words = diffWords(removed[p]!, added[p]!)
      if (words.similarity >= MIN_SIMILARITY) {
        beforeLine++
        afterLine++
        lines.push({
          op: 'changed',
          beforeLine,
          afterLine,
          before: removed[p]!,
          after: added[p]!,
          beforeParts: words.beforeParts,
          afterParts: words.afterParts,
        })
      } else {
        beforeLine++
        lines.push({ op: 'removed', beforeLine, afterLine: null, before: removed[p]!, after: null })
        afterLine++
        lines.push({ op: 'added', beforeLine: null, afterLine, before: null, after: added[p]! })
      }
    }
    for (let p = paired; p < removed.length; p++) {
      beforeLine++
      lines.push({ op: 'removed', beforeLine, afterLine: null, before: removed[p]!, after: null })
    }
    for (let p = paired; p < added.length; p++) {
      afterLine++
      lines.push({ op: 'added', beforeLine: null, afterLine, before: null, after: added[p]! })
    }
  }
  return lines
}

// Estrutura do documento ----------------------------------------------------------------------------

interface Unit {
  kind: 'section' | 'block'
  /** Chave de casamento entre as duas versões: o título sem número, ou o nome do bloco. */
  key: string
  label: string
  /** Texto da unidade sem espaços nas pontas, para comparar. */
  content: string
  line: number
}

/** O rótulo do bloco na comparação: o `title` escrito na linha `:::nome{title="…"}` ou o padrão. */
function blockLabel(name: string, line: string) {
  const custom = /\btitle=(?:"([^"]*)"|'([^']*)')/.exec(line)
  return getBlockTitle(name, { title: custom?.[1] ?? custom?.[2] ?? null }) ?? name
}

const normalizeKey = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()

/**
 * Divide o corpo em unidades comparáveis: os blocos do dialeto que vêm antes da primeira seção
 * (resumo, o que autoriza, riscos, partes) e cada seção `##` ou cláusula `###` com o texto até o
 * próximo título. A numeração é a do documento renderizado (`##` → 1, `###` → 1.1).
 */
function splitUnits(markdown: string): Unit[] {
  const { body } = splitFrontMatter(markdown)
  const offset = markdown.split('\n').length - body.split('\n').length
  const lines = body.split('\n')
  const units: Unit[] = []
  let current: { unit: Unit; buffer: string[] } | null = null
  let section = 0
  let clause = 0
  let sectionTitle = ''
  let inFence = false
  const seen = new Map<string, number>()

  const close = () => {
    if (!current) return
    current.unit.content = current.buffer.join('\n').trim()
    units.push(current.unit)
    current = null
  }
  const open = (kind: Unit['kind'], rawKey: string, label: string, line: number) => {
    close()
    // Dois títulos iguais no mesmo documento: o segundo ganha um sufixo para não casar com o primeiro.
    const base = `${kind}:${normalizeKey(rawKey)}`
    const count = (seen.get(base) ?? 0) + 1
    seen.set(base, count)
    current = {
      unit: { kind, key: count === 1 ? base : `${base}#${count}`, label, content: '', line },
      buffer: [],
    }
  }

  let beforeFirstSection = true
  let openBlock: string | null = null

  lines.forEach((text, index) => {
    const line = index + 1 + offset
    if (/^(```|~~~)/.test(text)) inFence = !inFence
    if (inFence) {
      current?.buffer.push(text)
      return
    }
    const heading = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(text)
    if (heading) {
      beforeFirstSection = false
      openBlock = null
      const title = heading[2]!.replace(/^\d+(\.\d+)*\.?\s+/, '')
      if (heading[1] === '##') {
        section++
        clause = 0
        sectionTitle = title
        open('section', title, `${section} ${title}`, line)
      } else {
        clause++
        open('section', `${sectionTitle}/${title}`, `${section}.${clause} ${title}`, line)
      }
      return
    }
    if (beforeFirstSection) {
      const directive = /^:::\s*([a-z][\w-]*)/.exec(text)
      if (directive && !openBlock) {
        openBlock = directive[1]!
        open('block', openBlock, blockLabel(openBlock, text), line)
        return
      }
      if (/^:::\s*$/.test(text) && openBlock) {
        openBlock = null
        close()
        return
      }
    }
    current?.buffer.push(text)
  })
  close()
  return units
}

const FRONT_MATTER_LABELS: Record<string, string> = {
  title: 'Título',
  type: 'Tipo',
  procedure: 'Procedimento',
}

/**
 * Compara duas versões do Markdown de um documento. `before` é a versão de origem (a vigente,
 * a anterior) e `after` a de destino (o rascunho, a nova).
 */
export function diffDocuments(before: string, after: string): DocumentDiff {
  const lines = diffLines(before, after)
  const stats = { addedLines: 0, removedLines: 0, changedLines: 0 }
  for (const line of lines) {
    if (line.op === 'added') stats.addedLines++
    else if (line.op === 'removed') stats.removedLines++
    else if (line.op === 'changed') stats.changedLines++
  }
  const isIdentical = before === after
  const summary: SummaryEntry[] = []
  if (isIdentical) return { isIdentical, summary, lines, stats }

  // Front matter: só o que é do documento. Rótulo, data e revisão mudam a cada versão e já
  // aparecem na lista de versões.
  const fmBefore = splitFrontMatter(before).data
  const fmAfter = splitFrontMatter(after).data
  for (const [key, label] of Object.entries(FRONT_MATTER_LABELS)) {
    const a = fmBefore[key]
    const b = fmAfter[key]
    if (String(a ?? '') === String(b ?? '')) continue
    summary.push({
      kind: 'front_matter',
      status: a === undefined ? 'added' : b === undefined ? 'removed' : 'changed',
      label,
      line: 1,
    })
  }

  // Blocos e seções.
  const unitsBefore = splitUnits(before)
  const unitsAfter = splitUnits(after)
  const byKeyBefore = new Map(unitsBefore.map((u) => [u.key, u]))
  const byKeyAfter = new Map(unitsAfter.map((u) => [u.key, u]))
  const commonBefore = unitsBefore.filter((u) => byKeyAfter.has(u.key)).map((u) => u.key)
  const commonAfter = unitsAfter.filter((u) => byKeyBefore.has(u.key)).map((u) => u.key)
  // O que mudou de lugar: as unidades comuns que não estão na maior ordem preservada.
  const kept = new Set(
    diffSequences(commonBefore, commonAfter)
      .filter((o) => o.op === 'equal')
      .map((o) => o.value),
  )

  for (const unit of unitsAfter) {
    const previous = byKeyBefore.get(unit.key)
    if (!previous)
      summary.push({ kind: unit.kind, status: 'added', label: unit.label, line: unit.line })
    else if (previous.content !== unit.content)
      summary.push({ kind: unit.kind, status: 'changed', label: unit.label, line: unit.line })
    else if (!kept.has(unit.key))
      summary.push({ kind: unit.kind, status: 'moved', label: unit.label, line: unit.line })
  }
  for (const unit of unitsBefore) {
    if (!byKeyAfter.has(unit.key))
      summary.push({ kind: unit.kind, status: 'removed', label: unit.label, line: unit.line })
  }

  // Variáveis.
  const variablesBefore = new Set(extractVariables(before))
  const variablesAfter = new Set(extractVariables(after))
  for (const path of variablesAfter) {
    if (!variablesBefore.has(path))
      summary.push({ kind: 'variable', status: 'added', label: `{{${path}}}`, line: null })
  }
  for (const path of variablesBefore) {
    if (!variablesAfter.has(path))
      summary.push({ kind: 'variable', status: 'removed', label: `{{${path}}}`, line: null })
  }

  return { isIdentical, summary, lines, stats }
}
